
import {
  normalizeSportsbookId,
  SPORTSBOOKS,
  type SportsbookDescriptor,
} from './sportsbook-registry';
import { sportsbookLogoUrl } from './sportsbook-logo-url';

const WEBP_STEMS = new Set(["DK", "UD", "HR", "MG", "P6"]);

function preferredLogoUrl(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, "");
  return sportsbookLogoUrl(WEBP_STEMS.has(stem) ? `${stem}.webp` : filename);
}

const LOGO_CODE_TO_DESCRIPTOR: Map<string, SportsbookDescriptor> = new Map();
for (const descriptor of Object.values(SPORTSBOOKS)) {
  const code = descriptor.logoCode.toLowerCase();
  if (!LOGO_CODE_TO_DESCRIPTOR.has(code)) {
    LOGO_CODE_TO_DESCRIPTOR.set(code, descriptor);
  }
}

export interface LogoResolutionOptions {
  fallback?: 'uppercase' | 'default';
}

export function resolveSportsbookDescriptor(input: string): SportsbookDescriptor | undefined {
  if (!input || typeof input !== 'string') {
    return undefined;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return undefined;
  }

  const normalizedId = normalizeSportsbookId(trimmed);
  if (normalizedId && SPORTSBOOKS[normalizedId]) {
    return SPORTSBOOKS[normalizedId];
  }

  const lowerInput = trimmed.toLowerCase();
  const descriptorByCode = LOGO_CODE_TO_DESCRIPTOR.get(lowerInput);
  if (descriptorByCode) {
    return descriptorByCode;
  }

  return undefined;
}

export function getSportsbookLogoFilename(
  input: string,
  options: LogoResolutionOptions = {}
): string {
  const { fallback = 'uppercase' } = options;

  const descriptor = resolveSportsbookDescriptor(input);
  
  if (descriptor) {
    return descriptor.logoFilename ?? `${descriptor.logoCode}.png`;
  }

  if (fallback === 'default') {
    return 'UPSIDE.png';
  }
  
  return `${input.toUpperCase()}.png`;
}

export type SportsbookLogoMimeType =
  | 'image/jpeg'
  | 'image/png'
  | 'image/svg+xml';

export function getSportsbookLogoMimeType(filename: string): SportsbookLogoMimeType {
  const extension = filename.slice(filename.lastIndexOf('.')).toLowerCase();

  switch (extension) {
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.svg':
      return 'image/svg+xml';
    default:
      throw new Error(`Unsupported sportsbook logo extension: ${extension || '(none)'}`);
  }
}

export function getSportsbookLogoPath(
  input: string,
  options: LogoResolutionOptions = {}
): string {
  const { fallback = 'uppercase' } = options;

  const descriptor = resolveSportsbookDescriptor(input);
  
  if (descriptor) {
    const filename = descriptor.logoFilename ?? `${descriptor.logoCode}.png`;
    return preferredLogoUrl(filename);
  }

  if (fallback === 'default') {
    return sportsbookLogoUrl('UPSIDE.png');
  }

  return sportsbookLogoUrl(`${input.toUpperCase()}.png`);
}
